import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { exportToCsv } from "shared/common/utils/export";
import { readFindTotal } from "shared/truncation";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import {
  DOCKER_HOST_EXPORT_COLUMNS,
  toDockerHostExportRow,
  type ExportRecord,
} from "../infrastructureExports";
import {
  isDockerHostDown,
  isDockerHostUp,
  type DockerHost,
} from "../infrastructure.types";
import { isSessionEnded } from "stores/sessionGuard";

export type DockerHostStatusFilter = "all" | "up" | "down" | "unknown";

/**
 * Page size for the host read. `docker-hosts` IS a real paginated KnexService
 * (unlike the `client-instances` discovery proxy), so this is a cap that the
 * server actually enforces — which is why {@link DockerHostsStore.serverTotal}
 * records the envelope's `total` and the view captions any shortfall instead of
 * quietly showing a partial fleet.
 */
export const DOCKER_HOST_PAGE_SIZE = 200;

/**
 * View store for the Docker Hosts (Portainer endpoints) control-plane view.
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore. The host list is read through the registered `docker-hosts`
 * ServiceStore (see serviceStores/infrastructure.stores.ts); `hosts` is a
 * computed view over that store's entity cache (never held locally).
 *
 * READ-ONLY (Phase 4): this view lists hosts and their health/status. There is
 * no create/edit/test here yet — host registration + the Portainer API key
 * (write-only, encrypted at rest by the API, NEVER returned: `hasApiKey` only)
 * land alongside the Phase 5 action seam. The secret is never read, logged, or
 * placed in the bundle.
 */
class DockerHostsStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;

  /**
   * `total` from the last find envelope — how many hosts the SERVER has, as
   * opposed to how many it sent. Null until a paginated response has been seen
   * (a service with pagination disabled returns a bare array and has no total
   * to report; the view then simply says nothing rather than guessing).
   *
   * This is a plain number, not an entity, so it does not violate the "no API
   * entities in a ViewStore" rule — the rows still live in the ServiceStore.
   */
  serverTotal: number | null = null;

  // Header filter
  searchQuery = "";
  statusFilter: DockerHostStatusFilter = "all";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `docker-hosts` ServiceStore. */
  private get hostsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("docker-hosts") ?? this.rootStore.dockerHostsStore) as unknown as ServiceStoreType | null) ?? null;
  }

  /**
   * Docker host list. Per the MobX rule, entities live in the ServiceStore cache
   * (populated by `find`), not in this ViewStore — this is a computed view.
   */
  get hosts(): DockerHost[] {
    return (this.hostsService?.entities ?? []) as unknown as DockerHost[];
  }

  // ---- Derived data -------------------------------------------------------

  hostName(id?: string | null): string {
    if (!id) return "";
    return this.hosts.find((h) => h.id === id)?.name || "";
  }

  get filteredHosts(): DockerHost[] {
    let result = this.hosts;

    if (this.statusFilter !== "all") {
      if (this.statusFilter === "up") {
        result = result.filter((h) => isDockerHostUp(h.status));
      } else if (this.statusFilter === "down") {
        result = result.filter((h) => isDockerHostDown(h.status));
      } else {
        // "unknown" = neither up nor down (incl. unprobed/empty status).
        result = result.filter(
          (h) => !isDockerHostUp(h.status) && !isDockerHostDown(h.status)
        );
      }
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      result = result.filter(
        (h) =>
          h.name?.toLowerCase().includes(q) ||
          h.portainerUrl?.toLowerCase().includes(q)
      );
    }

    return result;
  }

  get totalCount(): number {
    return this.hosts.length;
  }

  get upCount(): number {
    return this.hosts.filter((h) => isDockerHostUp(h.status)).length;
  }

  get downCount(): number {
    return this.hosts.filter((h) => isDockerHostDown(h.status)).length;
  }

  get unknownCount(): number {
    return this.hosts.filter(
      (h) => !isDockerHostUp(h.status) && !isDockerHostDown(h.status)
    ).length;
  }

  /** How many hosts survive the header filters — what the table renders. */
  /** True when a search or status filter is set — the only time the empty
   *  table may say "match the current filters". */
  get hasActiveFilter(): boolean {
    return this.searchQuery.trim().length > 0 || this.statusFilter !== "all";
  }

  get filteredCount(): number {
    return this.filteredHosts.length;
  }

  /**
   * True when the server holds more hosts than this page fetched — i.e. the
   * "Hosts" card and the table are BOTH short of reality. Only ever true once a
   * paginated envelope has reported a `total` we did not fully receive.
   */
  get isTruncated(): boolean {
    return this.serverTotal !== null && this.serverTotal > this.totalCount;
  }

  // ---- CSV export ---------------------------------------------------------

  /**
   * The host inventory as flat CSV records — the rows ON SCREEN
   * (`filteredHosts`, in the server's name order, which is what the table
   * renders since this view has no column sort).
   *
   * Carries NO credential-derived column: not the key, and not the screen's
   * "API Key" presence flag either. See the credential rule in
   * ../infrastructureExports.ts.
   */
  get exportRows(): ExportRecord[] {
    return this.filteredHosts.map(toDockerHostExportRow);
  }

  get canExport(): boolean {
    return this.exportRows.length > 0;
  }

  /** Download the on-screen hosts as CSV. False (never a throw) on an empty
   *  set; the header also disables the button on {@link canExport}. */
  exportCsv(): boolean {
    const rows = this.exportRows;
    if (rows.length === 0) return false;
    try {
      exportToCsv(rows, DOCKER_HOST_EXPORT_COLUMNS, {
        fileName: `docker-hosts-${new Date().toISOString().slice(0, 10)}`,
      });
      return true;
    } catch (err) {
      console.error("Docker hosts CSV export failed:", err);
      return false;
    }
  }

  // ---- Filter setters -----------------------------------------------------

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  setStatusFilter(filter: DockerHostStatusFilter) {
    this.statusFilter = filter;
  }

  // ---- Loading ------------------------------------------------------------

  async loadHosts() {
    this.loading = true;
    this.error = null;
    try {
      // ServiceStore.find populates its entity cache, which `hosts` reads.
      // The `$limit` here is REAL — `docker-hosts` is a paginated KnexService —
      // so capture the envelope's `total` and let the view disclose any
      // shortfall. Raising the cap would only move the lie further out.
      // findOrThrow: the ServiceStore resolves null on a failed find, which
      // rendered the "none yet" empty state and zero tiles.
      const result = await findOrThrow(this.hostsService, {
        query: { $limit: DOCKER_HOST_PAGE_SIZE, $sort: { name: 1 } },
      });
      const total = readFindTotal(result);
      runInAction(() => {
        this.serverTotal = total;
      });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = loadErrorMessage(err, "Failed to load Docker hosts");
      });
      console.error("Failed to load Docker hosts:", err);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** Alias used by header refresh / post-write callbacks. */
  async reload() {
    await this.loadHosts();
  }

  /** Ensure the list is populated once (used by consumers like the instances grid). */
  async ensureLoaded() {
    if (this.hosts.length === 0 && !this.loading) {
      await this.loadHosts();
    }
  }

  // -------------------------------------------------------------------------
  // PHASE 5 ACTION SEAM (intentionally not built in this read-only phase):
  //   - registerHost / editHost: create/patch `docker-hosts`. The Portainer
  //     API key is write-only — submitted on save, encrypted at rest by the
  //     API, NEVER returned (`hasApiKey` presence only) and cleared from form
  //     state after every save. Mirror DevOpsConnectionsStore's PAT handling.
  //   - testHost: POST `{ action: 'probe', id }` to re-run the health probe.
  //   No secret is ever sent from the client beyond the action + id.
  // -------------------------------------------------------------------------

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.loading = false;
    this.error = null;
    this.serverTotal = null;
    this.searchQuery = "";
    this.statusFilter = "all";
  }
}

export default DockerHostsStore;
